import React from "react";
import { Helmet } from "react-helmet-async";

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  name?: string;
  type?: string;
  image?: string;
  canonical?: string;
  noindex?: boolean;
  children?: React.ReactNode;
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords = "InnovaTech, Best Place for SIWES in Nigeria, SIWES Placement Jigawa Dutse, Best Tech Academy Jigawa, Top IT Consulting Agency Nigeria, Best Tech Partner Nigeria, Software Development, Web Development, Cybersecurity, CCTV Installation, IT Training Dutse",
  name = "InnovaTech",
  type = "website",
  image = "/logo/logo.png",
  canonical,
  noindex = false,
  children,
}) => {
  const siteUrl = "https://www.innovatech-ng.com";

  // Always build from siteUrl + canonical path so prerender gets the right URL.
  // Falls back to reading window.location only as a last resort (dev mode).
  const resolveUrl = (path?: string): string => {
    if (path) {
      return path.startsWith("http")
        ? path
        : `${siteUrl}${path.startsWith("/") ? path : `/${path}`}`;
    }
    if (typeof window !== "undefined") return window.location.href;
    return siteUrl;
  };

  const canonicalUrl = resolveUrl(canonical);
  const ogUrl = resolveUrl(canonical);

  return (
    <Helmet>
      {/* Basic Metadata */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="InnovaTech Consultancy Limited" />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Geo-Targeting Metadata (Nigeria & Jigawa State) */}
      <meta name="geo.region" content="NG-JI" />
      <meta name="geo.placename" content="Dutse, Jigawa State, Nigeria" />
      <meta name="geo.position" content="11.6928;9.3480" />
      <meta name="ICBM" content="11.6928, 9.3480" />

      {/* Canonical */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:locale" content="en_NG" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta
        property="og:image"
        content={image.startsWith("http") ? image : `${siteUrl}${image}`}
      />
      <meta property="og:url" content={ogUrl} />
      <meta property="og:site_name" content={name} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@innovatech_ng" />
      <meta name="twitter:creator" content="@innovatech_ng" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta
        name="twitter:image"
        content={image.startsWith("http") ? image : `${siteUrl}${image}`}
      />

      {/* Structured Data */}
      {children}
    </Helmet>
  );
};

export default SEO;
